import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Input,
  FormGroup,
  Table,
} from "reactstrap";
import { Image, P } from "../../../../AbstractElements";
import { EmailData } from "../types";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../../common-elements/breadcrumbs/Breadcrumbs";
import { dynamicImage } from "../../../../service";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import CommunicationPagination from "../../CommunicationPagination";

interface EmailDetailProps {
  email?: EmailData;
  onBack?: () => void;
}

const userData: {
  [key: string]: {
    value: string;
    label: string;
    role: string;
    avatar: string;
    email: string;
    phone: string;
  }[];
} = {
  sales: [
    {
      value: "john",
      label: "John Doe",
      role: "Sales",
      avatar: "dashboard-8/user3.png",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      value: "jane",
      label: "Jane Smith",
      role: "Sales",
      avatar: "dashboard-8/user2.png",
      email: "jane.smith@example.com",
      phone: "234-567-8901",
    },
  ],
  developers: [
    {
      value: "alice",
      label: "Alice Johnson",
      role: "Developers",
      avatar: "dashboard-8/user1.png",
      email: "alice.j@example.dev",
      phone: "345-678-9012",
    },
    {
      value: "bob",
      label: "Bob Wilson",
      role: "Developers",
      avatar: "dashboard-8/user2.png",
      email: "bob.w@example.dev",
      phone: "456-789-0123",
    },
    {
      value: "amal",
      label: "Amal",
      role: "Developers",
      avatar: "dashboard-8/user1.png",
      email: "amal@example.dev",
      phone: "567-890-1234",
    },
    {
      value: "dev1",
      label: "Dev One",
      role: "Developers",
      avatar: "dashboard-8/user1.png",
      email: "dev1@example.dev",
      phone: "111-222-3333",
    },
    {
      value: "dev2",
      label: "Dev Two",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev2@example.dev",
      phone: "222-333-4444",
    },
    {
      value: "dev3",
      label: "Dev Three",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev3@example.dev",
      phone: "333-444-5555",
    },
    {
      value: "dev4",
      label: "Dev Four",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev4@example.dev",
      phone: "444-555-6666",
    },
    {
      value: "dev5",
      label: "Dev Five",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev5@example.dev",
      phone: "555-666-7777",
    },
    {
      value: "dev6",
      label: "Dev Six",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev6@example.dev",
      phone: "666-777-8888",
    },
    {
      value: "dev7",
      label: "Dev Seven",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev7@example.dev",
      phone: "777-888-9999",
    },
    {
      value: "dev8",
      label: "Dev Eight",
      role: "Developers",
      avatar: "dashboard-8/user3.png",
      email: "dev8@example.dev",
      phone: "888-999-0000",
    },
  ],
  finance: [
    {
      value: "emma",
      label: "Emma Brown",
      role: "Finance",
      avatar: "dashboard-8/user2.png",
      email: "emma.brown@example.com",
      phone: "678-901-2345",
    },
  ],
};

const maskEmail = (email: string) => {
  const [localPart, domain] = email.split("@");
  if (!domain) return "invalid-email";
  if (localPart.length > 2) return `${localPart.substring(0, 2)}***@${domain}`;
  return `${localPart.substring(0, 1)}***@${domain}`;
};

const maskPhone = (phone: string) => {
  if (phone.length > 4) return `***-***-${phone.slice(-4)}`;
  return "***";
};

const EmailDetail: React.FC<EmailDetailProps> = ({ email, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [emailData, setEmailData] = useState<EmailData | undefined>(
    email || location.state?.email
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const handleBack =
    onBack || (() => navigate(`${process.env.PUBLIC_URL}/communication/email`));

  const allUsers = useMemo(() => Object.values(userData).flat(), []);

  const expandedRecipients = useMemo(() => {
    if (!emailData) return [];
    const detailedRecipients: typeof allUsers = [];
    const addedUsers = new Set<string>();
    emailData.recipients.forEach((recipient) => {
      const recipientLower = recipient.toLowerCase();
      if (userData[recipientLower]) {
        userData[recipientLower].forEach((member) => {
          if (!addedUsers.has(member.value)) {
            detailedRecipients.push(member);
            addedUsers.add(member.value);
          }
        });
      } else {
        const user = allUsers.find(
          (u) =>
            u.value.toLowerCase() === recipientLower ||
            u.label.toLowerCase() === recipientLower
        );
        if (user && !addedUsers.has(user.value)) {
          detailedRecipients.push(user);
          addedUsers.add(user.value);
        } else if (!user && !addedUsers.has(recipientLower)) {
          detailedRecipients.push({
            value: recipientLower,
            label: recipient,
            role: "External",
            avatar: "user/user.png",
            email: "unknown@example.com",
            phone: "000-000-0000",
          });
          addedUsers.add(recipientLower);
        }
      }
    });
    return detailedRecipients;
  }, [emailData, allUsers]);

  const filteredRecipients = useMemo(() => {
    if (!searchQuery) return expandedRecipients;
    return expandedRecipients.filter(
      (user) =>
        user.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.role.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, expandedRecipients]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  useEffect(() => {
    if (email || location.state?.email) {
      setEmailData(email || location.state?.email);
    }
  }, [email, location.state]);

  if (!emailData) {
    return (
      <div className="page-body">
        <Container fluid>
          <Card>
            <CardBody>
              <P>No Email Selected</P>
              <P>Please select an email to view its details.</P>
              <Button color="primary" onClick={handleBack}>
                Back to Inbox
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  const sanitizedSubMessage = DOMPurify.sanitize(emailData.subMessage || "");

  const indexOfLastRecipient = currentPage * pageSize;
  const indexOfFirstRecipient = indexOfLastRecipient - pageSize;
  const currentRecipients = filteredRecipients.slice(
    indexOfFirstRecipient,
    indexOfLastRecipient
  );

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={"Email Details"}
        parents={[
          {
            label: "Communication",
            link: `${process.env.PUBLIC_URL}/communication/email`,
          },
          {
            label: "Email",
            link: `${process.env.PUBLIC_URL}/communication/email`,
          },
        ]}
      />
      <Container fluid>
        <Row>
          <Col xxl="12">
            <Card className="email-detail-card">
              <CardBody className="p-4">
                <div className="email-detail-content">
                  <Row className="email-field mb-3">
                    <Col
                      xs="auto"
                      className="field-label d-flex align-items-center"
                    >
                      <div className="mb-0">Subject:</div>
                    </Col>
                    <Col>
                      <div className="mb-0 text-primary">
                        {emailData.message}
                      </div>
                    </Col>
                  </Row>
                  <Row className="email-field mb-3">
                    <Col
                      xs="auto"
                      className="field-label d-flex align-items-center"
                    >
                      <div className="mb-0">From:</div>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center gap-3">
                        <div className="sender-avatar">
                          <Image
                            src={dynamicImage(
                              "dashboard-8/Linnk_Group_logo.png"
                            )}
                            alt="Linnk Group Logo"
                            style={{ width: "40px", height: "auto" }}
                          />
                        </div>
                        <div className="sender-info">
                          <P className="mb-0 fw-medium">Linnk Group</P>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row className="email-field mb-3">
                    <Col
                      xs="auto"
                      className="field-label d-flex align-items-center"
                    >
                      <div className="mb-0">Time:</div>
                    </Col>
                    <Col>
                      <P className="mb-0 text-muted small">{emailData.time}</P>
                    </Col>
                  </Row>

                  <Row className="email-field mb-4">
                    <Col
                      xs="auto"
                      className="field-label d-flex align-items-start"
                    >
                      <div className="mb-0 mt-1">Tags:</div>
                    </Col>
                    <Col>
                      {emailData.badge && emailData.badge.length > 0 ? (
                        <div className="tags-list d-flex flex-wrap gap-2">
                          {emailData.badge.map((item, index) => (
                            <Badge
                              key={index}
                              color=""
                              className={`badge-light-${item.color} px-3 py-2`}
                            >
                              {item.title}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <P className="mb-0 text-muted">No tags assigned</P>
                      )}
                    </Col>
                  </Row>

                  <div className="email-field mt-4">
                    <div className="field-label mb-3">Message Content:</div>
                    <div className="field-content">
                      <Card className="message-content-card border-0">
                        <CardBody className="p-4">
                          <div className="message-text">
                            <ReactMarkdown>{sanitizedSubMessage}</ReactMarkdown>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>

                  <div className="email-field mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="field-label">Recipient Status:</div>
                      <FormGroup className="mb-0" style={{ maxWidth: "300px" }}>
                        <Input
                          type="text"
                          placeholder="Search user"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </FormGroup>
                    </div>

                    <div className="field-content">
                      <Card className="recipient-status-card border-0">
                        <div className="table-responsive">
                          <Table className="align-middle" hover>
                            <thead className="table-light">
                              <tr>
                                <th scope="col" style={{ padding: "1rem" }}>
                                  User
                                </th>
                                <th scope="col" style={{ padding: "1rem" }}>
                                  Role
                                </th>
                                <th scope="col" style={{ padding: "1rem" }}>
                                  Email
                                </th>
                                <th scope="col" style={{ padding: "1rem" }}>
                                  Phone
                                </th>
                                <th scope="col" style={{ padding: "1rem" }}>
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentRecipients.length > 0 ? (
                                currentRecipients.map((recipient, index) => (
                                  <tr key={`${recipient.value}-${index}`}>
                                    <td style={{ padding: "1rem" }}>
                                      <div className="d-flex align-items-center">
                                        <Image
                                          className="img-fluid rounded-circle me-3"
                                          src={dynamicImage(recipient.avatar)}
                                          alt={`${recipient.label} avatar`}
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                          }}
                                        />
                                        <span className="fw-medium">
                                          {recipient.label}
                                        </span>
                                      </div>
                                    </td>
                                    <td style={{ padding: "1rem" }}>
                                      {recipient.role}
                                    </td>
                                    <td style={{ padding: "1rem" }}>
                                      <span className="text-muted">
                                        {maskEmail(recipient.email)}
                                      </span>
                                    </td>
                                    <td style={{ padding: "1rem" }}>
                                      <span className="text-muted">
                                        {maskPhone(recipient.phone)}
                                      </span>
                                    </td>
                                    <td style={{ padding: "1rem" }}>
                                      {(indexOfFirstRecipient + index) % 2 ===
                                      0 ? (
                                        <Badge
                                          color="light-success"
                                          className="text-success"
                                          pill
                                        >
                                          Delivered
                                        </Badge>
                                      ) : (
                                        <Badge
                                          color="light-warning"
                                          className="text-warning"
                                          pill
                                        >
                                          Pending
                                        </Badge>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={5}>
                                    <P className="text-center text-muted my-4">
                                      No matching recipients found.
                                    </P>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </Card>

                      <CommunicationPagination
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalItems={filteredRecipients.length}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EmailDetail;
